import { InputsReportContext, ReportDefinition, ReportEvaluation, ReportValues, RunReportContext, SignoffSpec } from './types';
import { InputSheet, TypedInputSheet } from './inputs';
import { RunSnapshot, RunTraceRequest } from './runs';
import { Requirement, ResolvedBinding, resolveBindings } from './bindings';
import { ValuesSchema, defineValues } from './schema';

/**
 * Defining a run report from schemas and a judgement, for a report whose
 * document is written by hand (a composed report uses defineComposedReport).
 *
 *   export const definition = defineRunReport({
 *     key: 'my-report', title: 'My report', description: '…', version: '1.0.0',
 *     values: defineValues({ conclusion: v.text({ label: 'Conclusion', multiline: true }) }),
 *     settings: defineValues({ limit: v.number({ label: 'Upper limit', unit: 'A' }) }),
 *     requires: { current: need.number('Measured current', { role: 'measurement' }) },
 *     signoffs: [{ id: 'engineer', label: 'Test engineer' }],
 *     judge: ({ settings, bindings }) => verdictFrom([
 *       finding.check('within', (boundNumber(bindings.current) ?? Infinity) <= (settings.limit ?? -Infinity), 'Within the limit', 'Over the limit'),
 *     ]),
 *   });
 *
 * `judge` returns only the verdict, its label and the findings. The document
 * derives whatever else it prints from the same values, settings and run,
 * through `definition.read(values, context)`.
 */

export interface RunJudgeInput<V, S> {
  values: V;
  settings: S;
  context: RunReportContext;
  run: RunSnapshot;
  /** the report's `requires`, resolved against the run and the report's binding table */
  bindings: Record<string, ResolvedBinding>;
}

export interface RunReportSpec<V, S> {
  key: string;
  title: string;
  description: string;
  version: string;
  values: ValuesSchema<V>;
  settings?: ValuesSchema<S>;
  requires?: Record<string, Requirement>;
  signoffs?: SignoffSpec[];
  traces?: (run: RunSnapshot) => RunTraceRequest[];
  judge: (input: RunJudgeInput<V, S>) => ReportEvaluation;
}

export type SchemaRunReportDefinition<V extends object, S> = ReportDefinition<V, ReportEvaluation, RunReportContext> & {
  valuesSchema: ValuesSchema<V>;
  settingsSchema: ValuesSchema<S>;
  /** what `judge` reads, for the document to print from the same */
  read: (values: V, context: RunReportContext) => RunJudgeInput<V, S>;
};

const EMPTY = defineValues({});

export function defineRunReport<V extends object, S = Record<string, never>>(spec: RunReportSpec<V, S>): SchemaRunReportDefinition<V, S> {
  const settingsSchema = (spec.settings ?? EMPTY) as ValuesSchema<S>;
  const bindingsOf = (settings: S): Record<string, unknown> => {
    const b = (settings as unknown as Record<string, unknown>).bindings;
    return b && typeof b === 'object' ? (b as Record<string, unknown>) : {};
  };
  const read = (values: V, context: RunReportContext): RunJudgeInput<V, S> => {
    const settings = settingsSchema.normalize(context.settings ?? {});
    return { values: spec.values.normalize(values), settings, context, run: context.run, bindings: resolveBindings(spec.requires, context.run, bindingsOf(settings) as never) };
  };
  const judge = (values: V, context: RunReportContext) => spec.judge(read(values, context));
  return {
    key: spec.key,
    subject: 'run',
    title: spec.title,
    description: spec.description,
    version: spec.version,
    defaultValues: spec.values.defaults,
    normalizeValues: spec.values.normalize,
    defaultSettings: () => settingsSchema.defaults() as ReportValues,
    normalizeSettings: (raw) => settingsSchema.normalize(raw) as ReportValues,
    requires: spec.requires,
    signoffs: spec.signoffs,
    traces: spec.traces,
    judge,
    evaluate: judge,
    valuesSchema: spec.values,
    settingsSchema,
    read,
  };
}

/**
 * Defining a report filled in from its input sheet. The sheet is the bundle's
 * `inputs.json`, imported typed from the `inputs.generated.ts` the server
 * writes beside it, so `inputs` arrives as the sheet's own type:
 *
 *   import { sheet } from './inputs.generated';
 *   export const definition = defineInputsReport({
 *     key: 'my-report', title: 'My report', description: '…', version: '1.0.0',
 *     sheet,
 *     values: defineValues({ remarks: v.text({ label: 'Remarks', multiline: true }), signoffs: v.map(v.text(), { hidden: true }) }),
 *     signoffs: [{ id: 'engineer', label: 'Test engineer' }],
 *     judge: ({ inputs }) => verdictFrom([
 *       finding.check('temp', (inputs.ambient_temperature ?? 99) < 30, 'Tested at room temperature', 'Tested above 30 °C'),
 *     ]),
 *   });
 *
 * `values` are the report's own — what is asked on the report itself, such as
 * remarks and sign-offs — not the sheet's inputs.
 */

export interface InputsJudgeInput<I, V, S> {
  /** what was entered on the sheet, normalized to it */
  inputs: I;
  values: V;
  settings: S;
  sheet: InputSheet;
  context: InputsReportContext;
}

export interface InputsReportSpec<I, V, S> {
  key: string;
  title: string;
  description: string;
  version: string;
  sheet: TypedInputSheet<I>;
  values: ValuesSchema<V>;
  settings?: ValuesSchema<S>;
  signoffs?: SignoffSpec[];
  judge: (input: InputsJudgeInput<I, V, S>) => ReportEvaluation;
}

export type InputsReportDefinition<I, V extends object, S> = ReportDefinition<V, ReportEvaluation, InputsReportContext> & {
  valuesSchema: ValuesSchema<V>;
  settingsSchema: ValuesSchema<S>;
  /** what `judge` reads, for the document to print from the same */
  read: (values: V, context: InputsReportContext) => InputsJudgeInput<I, V, S>;
};

export function defineInputsReport<I, V extends object, S = Record<string, never>>(spec: InputsReportSpec<I, V, S>): InputsReportDefinition<I, V, S> {
  const settingsSchema = (spec.settings ?? EMPTY) as ValuesSchema<S>;
  const read = (values: V, context: InputsReportContext): InputsJudgeInput<I, V, S> => ({
    inputs: context.inputs as I,
    values: spec.values.normalize(values),
    settings: settingsSchema.normalize(context.settings ?? {}),
    sheet: context.sheet,
    context,
  });
  const judge = (values: V, context: InputsReportContext) => spec.judge(read(values, context));
  return {
    key: spec.key,
    subject: 'inputs',
    title: spec.title,
    description: spec.description,
    version: spec.version,
    defaultValues: spec.values.defaults,
    normalizeValues: spec.values.normalize,
    defaultSettings: () => settingsSchema.defaults() as ReportValues,
    normalizeSettings: (raw) => settingsSchema.normalize(raw) as ReportValues,
    signoffs: spec.signoffs,
    judge,
    evaluate: judge,
    valuesSchema: spec.values,
    settingsSchema,
    read,
  };
}

/** A definition's settings for a context: normalized by the definition where it has settings, the context's as stored otherwise. */
export function settingsFor(definition: Pick<ReportDefinition<object, ReportEvaluation, RunReportContext>, 'normalizeSettings'>, context: { settings?: ReportValues }): ReportValues {
  return definition.normalizeSettings ? definition.normalizeSettings(context.settings ?? {}) : (context.settings ?? {});
}

/** The verdict of record for a definition: its `judge` where it has one, `evaluate` otherwise. The server publishes this. */
export function judgeReport<V extends object, M extends ReportEvaluation, C extends object>(definition: ReportDefinition<V, M, C>, values: V, context: C): ReportEvaluation {
  return definition.judge ? definition.judge(values, context) : definition.evaluate(values, context);
}
